import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DexfSettingsEnvironmentEnum } from '@inter-ikea-kompis/enums';
import { ProductOptionalFieldNameEnum } from '@inter-ikea-kompis/enums/lib';
import { SettingsService } from '@inter-ikea-kompis/services';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ITheme } from '@inter-ikea-kompis/types';
import { getDexfApiKey } from '@inter-ikea-storageone/range';
import { e2e, getQuery, getRange, stripURLOfQueryParameters } from 'utils/queryUtils';
import { getCountry, getLanguage, getLocale } from 'i18n/i18nUtils';
import { getEnvironment, isKiosk } from 'utils/platform';
import { Range, rangeNameMap, rangePrefixMap } from 'utils/range';
import { tryParse } from 'utils/dataUtils';
import FallbackScreen from 'components/FallbackScreen/FallbackScreen';
import {
  getDexfSettingsFilterSegment,
  getServiceInfo,
  productFields,
  replaceDoubleCurlyBrackets,
  getPhraseTranslations,
  getTranslationEndpoints
} from './utils';
import {
  KompisSettings,
  IStorageOneDexfSettings,
  KompisServices,
  IStorageOneTranslations,
  DexfGeneralSettings
} from './types';

type ServiceSettingsContextValue = {
  range: Range;
  rangeName: string;
  kompisSettings: KompisSettings;
  dexfSettings: IStorageOneDexfSettings;
  productFields: ProductOptionalFieldNameEnum[];
  translations: IStorageOneTranslations;
  services: KompisServices;
  theme: ITheme;
  dexfEndpoints: {
    baseUrl: string;
    itemList: string;
  };
};

export const defaultKompisSettings: KompisSettings = {
  consumer: 'StorageOne',
  contract: '42079',
  locale: getLocale(),
  applicationName: `storageonepax`, // used by VPC
  applicationNameTranslated: '',
  translationEndpointApplication: 'storageone',
  translationPrefixApplication: 'pax',
  dexfApplicationId: `storageonepax`, // storageonepax, storageonebesta
  uiPlatform: 'oneweb',
  environment: getEnvironment(),
  dexfApiKey: '',
  localizationPrefixApplication: 'PAX',
  localizationEndpoints: [],
  translationEndpoints: [],
  filterSegment: getDexfSettingsFilterSegment(),
  settings: {} as IStorageOneDexfSettings
};

const ServiceSettingsContext = React.createContext<ServiceSettingsContextValue>({
  range: 'pax' as Range,
  rangeName: 'PAX',
  kompisSettings: defaultKompisSettings,
  services: {
    shoppingListSupported: true,
    shoppingCartSupported: true
  },
  productFields: productFields,
  theme: SkapaTheme,
  dexfEndpoints: { baseUrl: '', itemList: '' },
  translations: {} as IStorageOneTranslations,
  dexfSettings: {} as IStorageOneDexfSettings
});

export const useServiceSettings = () => useContext(ServiceSettingsContext);

export const getDexfSettings = async () => {
  const query = getQuery();
  const range = getRange();
  const locale = getLocale();
  const dexfApiKey = getDexfApiKey(range);
  const dexfSettingsFilterSegment = getDexfSettingsFilterSegment();

  const settingsService = new SettingsService({
    dexfApiKey: dexfApiKey,
    dexfEnvironment: DexfSettingsEnvironmentEnum.production,
    dexfApplicationId: `storageone${range}`,
    locale: locale
  });

  const dexfSettings = (await settingsService.getApplicationSettings(
    dexfSettingsFilterSegment
  )) as IStorageOneDexfSettings;

  // Override the dexfSettings.general properties if specified in the URL query parameters
  [...query.keys()]
    .map(key => key as keyof DexfGeneralSettings)
    .forEach(key => {
      if (dexfSettings?.general && Object.keys(dexfSettings.general).includes(key)) {
        type SettingValue = DexfGeneralSettings[typeof key];
        const value = tryParse<SettingValue>(query.get(key));

        if (value === null) return;

        (dexfSettings.general as Record<typeof key, SettingValue>)[key] = value;
      }
    });

  const unitSystem = getQuery().get('unitSystem');

  dexfSettings.localisation.useMetricMeasures =
    unitSystem === 'imperial'
      ? false
      : unitSystem === 'metric'
      ? true
      : dexfSettings.localisation.useMetricMeasures;

  // Force save to profile enabled in e2e and disabled on kiosk
  dexfSettings.general.enableSaveToProfile =
    (dexfSettings.general.enableSaveToProfile && !isKiosk()) || e2e();

  return dexfSettings;
};

const getTranslations = async () => {
  const translations = await getPhraseTranslations(getLocale());

  return Object.assign(translations, {
    storageone: replaceDoubleCurlyBrackets(translations.storageone)
  });
};

const getAllSettings = async () => {
  const [dexfSettings, translations] = await Promise.all([getDexfSettings(), getTranslations()]);

  const range = getRange();
  const locale = getLocale();
  const rangeName = rangeNameMap[range];
  const countryCode = getCountry(locale);
  const languageCode = getLanguage(locale);
  const dexfApiKey = getDexfApiKey(range, false);
  const applicationName = `storageone${range}`;
  const services = getServiceInfo(dexfSettings);
  const translationEndpoints = getTranslationEndpoints(countryCode, languageCode);
  const redirectUri = stripURLOfQueryParameters(['state', 'code']);

  return {
    range,
    rangeName,
    kompisSettings: {
      ...defaultKompisSettings,
      applicationName: applicationName, // used by VPC
      localizationPrefixApplication: rangePrefixMap[range],
      translationPrefixApplication: rangePrefixMap[range],
      dexfApiKey,
      dexfApplicationId: applicationName, // storageonepax, storageonebesta
      translationEndpoints,
      redirectUri,
      settings: dexfSettings
    },
    services,
    dexfEndpoints: {
      baseUrl: 'https://api.dexf.ikea.com/',
      itemList: `https://api.dexf.ikea.com/vpc/v1/itemlist/retailunit/${countryCode}/locale/${locale}/`
    },
    productFields,
    dexfSettings,
    translations,
    theme: SkapaTheme
  };
};

export const ServiceSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, isLoading, error } = useQuery({
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryKey: ['serviceSettings'],
    queryFn: getAllSettings
  });

  if (isLoading) return null;

  if (error) {
    return (
      <FallbackScreen
        title={({ formatMessage }) =>
          formatMessage({ id: 'storageone.fallbackPlannerUnavailableTitle' })
        }
        body={({ formatMessage }) =>
          e2e() ? error.message : formatMessage({ id: 'storageone.fallbackPlannerUnavailableBody' })
        }
        button={({ formatMessage }) =>
          formatMessage({ id: 'storageone.fallbackPlannerUnavailableButton' })
        }
        onClick={() => {
          window.history.back();
        }}
      />
    );
  }

  if (!data) return null;

  return <ServiceSettingsContext.Provider value={data}>{children}</ServiceSettingsContext.Provider>;
};
